import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  IconButton,
  Image,
  Icon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FaEdit,
  FaPlus,
  FaEuroSign,
  FaTrash,
  FaInfoCircle,
} from 'react-icons/fa';
import { fetchBazars, deleteBazar } from '../../utils/bazarApi';
import { useAuth } from '../../contexts/AuthContext';

// Helper per ottenere il colore del badge stato
const getStatoColor = (stato) => {
  const statoColors = {
    disponibile: 'green',
    prenotato: 'yellow',
    venduto: 'gray',
    terminato: 'gray',
  };
  return statoColors[stato] || 'gray';
};

// Helper per ottenere il testo del badge stato
const getStatoLabel = (stato) => {
  const statoLabels = {
    disponibile: 'Disponibile',
    prenotato: 'Prenotato',
    venduto: 'Venduto',
    terminato: 'Terminato',
  };
  return statoLabels[stato] || stato;
};

// Helper per ottenere l'immagine principale
const getMainImage = (bazar) => {
  try {
    // Prova diverse strutture possibili - controlla PRIMA bazar.galleria direttamente se è un array
    let galleria = [];
    if (Array.isArray(bazar.galleria) && bazar.galleria.length > 0) {
      galleria = bazar.galleria;
    } else if (
      Array.isArray(bazar.attributes?.galleria) &&
      bazar.attributes.galleria.length > 0
    ) {
      galleria = bazar.attributes.galleria;
    } else if (
      bazar.attributes?.galleria?.data &&
      Array.isArray(bazar.attributes.galleria.data)
    ) {
      galleria = bazar.attributes.galleria.data;
    } else if (bazar.galleria?.data && Array.isArray(bazar.galleria.data)) {
      galleria = bazar.galleria.data;
    }

    if (galleria.length === 0) {
      return null;
    }

    const firstImage = galleria[0];

    const imageData = firstImage.attributes || firstImage;

    const url =
      imageData.url ||
      imageData.formats?.large?.url ||
      imageData.formats?.medium?.url ||
      imageData.formats?.small?.url ||
      imageData.formats?.thumbnail?.url;

    if (url) {
      // Se l'URL inizia già con http/https, restituiscilo così com'è
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      // Se l'URL inizia con /uploads, potrebbe funzionare come relativo (tramite proxy in develop)
      // In produzione, se Strapi è su dominio diverso, serve l'URL completo
      const baseUrl =
        process.env.REACT_APP_STRAPI_API_URL ||
        process.env.REACT_APP_STRAPI_URL;

      // Se abbiamo un baseUrl configurato, usalo
      if (baseUrl) {
        const cleanBaseUrl = baseUrl.replace(/\/$/, '');
        const cleanImageUrl = url.startsWith('/') ? url : `/${url}`;
        const finalUrl = `${cleanBaseUrl}${cleanImageUrl}`;

        return finalUrl;
      }

      // Se non c'è baseUrl, prova come URL relativo (funziona se Strapi è sullo stesso dominio o tramite proxy)
      const cleanImageUrl = url.startsWith('/') ? url : `/${url}`;

      return cleanImageUrl;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export default function Bazar() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const [bazars, setBazars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [bazarToDelete, setBazarToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadUserBazars = async () => {
    try {
      setLoading(true);
      // Usa il filtro "mine" lato server: restituisce solo gli oggetti del socio dell'utente loggato
      const result = await fetchBazars(
        {
          mine: true,
          stato: ['disponibile', 'prenotato', 'venduto', 'terminato'],
        },
        1,
        1000
      );
      setBazars(result.bazars || []);
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile caricare i tuoi oggetti',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadUserBazars();
    }
  }, [isAuthenticated]);

  const handleEdit = (bazar) => {
    const documentId = bazar.documentId || bazar.id;
    navigate(`/bazar/${documentId}/modifica`);
  };

  const handleView = (bazar) => {
    const documentId = bazar.documentId || bazar.id;
    navigate(`/bazar/${documentId}`);
  };

  const handleCreateNew = () => {
    navigate('/bazar/nuovo');
  };

  const handleDeleteClick = (bazar) => {
    setBazarToDelete(bazar);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!bazarToDelete) return;

    try {
      setDeleting(true);
      const documentId = bazarToDelete.documentId || bazarToDelete.id;
      await deleteBazar(documentId);

      toast({
        title: 'Oggetto eliminato',
        description: "L'oggetto è stato eliminato con successo",
        status: 'success',
        duration: 3000,
      });

      // Ricarica la lista
      await loadUserBazars();
      onClose();
      setBazarToDelete(null);
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || "Impossibile eliminare l'oggetto",
        status: 'error',
        duration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    onClose();
    setBazarToDelete(null);
  };

  if (loading) {
    return (
      <Center py={12}>
        <Spinner size="xl" color="brand.100" />
      </Center>
    );
  }

  return (
    <Box
      bg="#2f3136"
      p={{ base: 6, md: 8 }}
      borderRadius="20px"
      boxShadow="0 20px 40px rgba(0,0,0,0.3)"
      border="1px solid"
      borderColor="gray.600"
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        gap={3}
        mb={4}
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Heading size="lg" id="bazar-title" color="white" fontWeight="800">
          I Miei Oggetti nel Bazar
        </Heading>
        <Button
          leftIcon={<FaPlus />}
          bg="#ffcc00"
          color="black"
          _hover={{ bg: '#e6b800', transform: 'translateY(-2px)' }}
          borderRadius="12px"
          fontWeight="600"
          onClick={handleCreateNew}
        >
          Aggiungi nuovo oggetto
        </Button>
      </Flex>
      <Text color="gray.300" mb={6}>
        Gestisci i tuoi oggetti in vendita nel Bazar
      </Text>

      {/* Lista oggetti */}
      {bazars.length === 0 ? (
        <Box
          bg="#36393f"
          p={8}
          borderRadius="12px"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.600"
          textAlign="center"
        >
          <Icon as={FaInfoCircle} boxSize={8} mb={4} color="#ffcc00" />
          <Text color="gray.300" mb={2} fontWeight="600">
            Nessun oggetto nel Bazar
          </Text>
          <Text color="gray.400" fontSize="sm">
            Inizia ad aggiungere oggetti cliccando sul pulsante &quot;Aggiungi
            nuovo oggetto&quot;
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {bazars.map((bazar, index) => {
            const bazarData = bazar.attributes || bazar;
            const { documentId, id } = bazar;
            const finalDocumentId = documentId || id;
            const { titolo, prezzo, stato, categoria, prezzoNegoziabile } =
              bazarData;
            const finalTitolo = titolo || 'Senza titolo';
            const finalStato = stato || 'disponibile';
            const mainImage = getMainImage(bazar);

            return (
              <Box
                key={finalDocumentId}
                bg="#36393f"
                data-testid={`account-bazar-item-${index}`}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.600"
                overflow="hidden"
                boxShadow="sm"
                _hover={{
                  boxShadow: 'md',
                  transform: 'translateY(-2px)',
                  borderColor: '#ffcc00',
                }}
                transition="all 0.2s"
              >
                {/* Immagine */}
                <Box
                  position="relative"
                  w="100%"
                  h="200px"
                  bg="gray.700"
                  overflow="hidden"
                >
                  {mainImage ? (
                    <Image
                      src={mainImage}
                      alt={finalTitolo}
                      objectFit="cover"
                      w="100%"
                      h="100%"
                      filter={
                        finalStato === 'terminato' || finalStato === 'venduto'
                          ? 'grayscale(100%)'
                          : 'none'
                      }
                      opacity={
                        finalStato === 'terminato' || finalStato === 'venduto'
                          ? 0.7
                          : 1
                      }
                      transition="all 0.3s"
                    />
                  ) : (
                    <Center h="100%" color="gray.500">
                      <Text>Nessuna immagine</Text>
                    </Center>
                  )}
                  {/* Badge stato */}
                  <Badge
                    position="absolute"
                    top={2}
                    right={2}
                    colorScheme={getStatoColor(finalStato)}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {getStatoLabel(finalStato)}
                  </Badge>
                </Box>

                {/* Contenuto */}
                <VStack spacing={3} align="stretch" p={4}>
                  {/* Titolo */}
                  <Heading size="sm" noOfLines={2} color="white">
                    {finalTitolo}
                  </Heading>

                  {/* Prezzo */}
                  {prezzo !== null && prezzo !== undefined ? (
                    parseFloat(prezzo) === 0 ? (
                      <Badge
                        bg="#3eb297"
                        color="white"
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        Regalo!
                      </Badge>
                    ) : (
                      <HStack spacing={2}>
                        <Icon as={FaEuroSign} color="#ffcc00" />
                        <Text fontSize="lg" fontWeight="bold" color="white">
                          {parseFloat(prezzo).toFixed(2)}
                        </Text>
                        {prezzoNegoziabile && (
                          <Badge
                            bg="#ff6b35"
                            color="white"
                            fontSize="xs"
                            borderRadius="full"
                          >
                            Negoziabile
                          </Badge>
                        )}
                      </HStack>
                    )
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      Prezzo non specificato
                    </Text>
                  )}

                  {/* Categoria */}
                  {categoria && (
                    <Badge
                      bg="#36a9e1"
                      color="white"
                      fontSize="xs"
                      alignSelf="flex-start"
                      borderRadius="full"
                      px={2}
                      py={1}
                    >
                      {categoria}
                    </Badge>
                  )}

                  {/* Bottoni azione */}
                  <HStack spacing={2} mt={2}>
                    <Button
                      size="sm"
                      bg="transparent"
                      color="gray.300"
                      border="1px solid"
                      borderColor="gray.600"
                      flex={1}
                      _hover={{
                        bg: '#36393f',
                        borderColor: '#ffcc00',
                        color: 'white',
                      }}
                      borderRadius="8px"
                      onClick={() => handleView(bazar)}
                    >
                      Vedi
                    </Button>
                    <IconButton
                      icon={<FaEdit />}
                      aria-label="Modifica"
                      size="sm"
                      bg="#ffcc00"
                      color="black"
                      _hover={{ bg: '#e6b800' }}
                      borderRadius="8px"
                      onClick={() => handleEdit(bazar)}
                      data-testid={`bazar-edit-button-${index}`}
                    />
                    <IconButton
                      icon={<FaTrash />}
                      aria-label="Elimina"
                      size="sm"
                      bg="transparent"
                      color="red.400"
                      border="1px solid"
                      borderColor="red.400"
                      _hover={{
                        bg: 'red.500',
                        color: 'white',
                        borderColor: 'red.500',
                      }}
                      borderRadius="8px"
                      onClick={() => handleDeleteClick(bazar)}
                      data-testid={`bazar-delete-button-${index}`}
                    />
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* AlertDialog per conferma eliminazione */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={undefined}
        onClose={handleDeleteCancel}
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            bg="#36393f"
            border="1px solid"
            borderColor="gray.600"
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
              Conferma eliminazione
            </AlertDialogHeader>

            <AlertDialogBody color="gray.300">
              Sei sicuro di voler eliminare questo oggetto? Questa azione non
              può essere annullata.
              {bazarToDelete && (
                <Text mt={2} fontWeight="bold" color="white">
                  &quot;
                  {(bazarToDelete.attributes || bazarToDelete).titolo ||
                    'Oggetto senza titolo'}
                  &quot;
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                onClick={handleDeleteCancel}
                bg="transparent"
                color="gray.300"
                border="1px solid"
                borderColor="gray.600"
                _hover={{ bg: '#36393f', borderColor: '#ffcc00' }}
                mr={3}
              >
                Annulla
              </Button>
              <Button
                bg="red.500"
                color="white"
                _hover={{ bg: 'red.600' }}
                onClick={handleDeleteConfirm}
                isLoading={deleting}
                loadingText="Eliminazione..."
              >
                Elimina
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
